import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import { DataGrid, type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid";
import Button from "@components/Button/Button";
import { dataGridSx, dataGridWrapperSx } from "@components/DataTable/styles";
import { NotificationContext } from "@context/NotificationContext";
import { getTerminalServiceErrorMessage } from "@features/terminal-services/hooks/useTerminalServiceDocumentUpload";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import {
  FottServiceRequestStatus,
  FottServiceType,
  type FottServiceType as FottServiceTypeValue,
  type TerminalServiceUsageEntry,
} from "@services/terminal-services/types";
import {
  deleteTerminalServiceUsageHours,
  submitTerminalServiceUsageHours,
  updateTerminalServiceUsageHours,
} from "@services/terminalServicesApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { formatTerminalServiceDateTime } from "../utils";
import UsageDrawer, {
  calculateUsageHours,
  formatLocalDateTime,
  formatUsageHours,
  toDateTimeInputValue,
  toUtcDateTime,
  type UsageDrawerSubmitValues,
} from "./UsageDrawer";

const LANG_BASE = "terminal-services.details.usage";
const TEST_ID_BASE = "terminal-service-usage";
const FOTT_USER_ROLE = "fott-user";
const MAX_USAGE_ROWS = 100;
const VISIBLE_USAGE_ROWS = 20;
const ROW_HEIGHT_PX = 40;
const ACTION_COLUMN_WIDTH_PX = 96;
const TABLE_MIN_WIDTH_PX = 160 + 160 + 120 + ACTION_COLUMN_WIDTH_PX;
const PRESSURE_TABLE_MIN_WIDTH_PX = 160 + 160 + 120 + 120 + ACTION_COLUMN_WIDTH_PX;
const EMPTY_ROLES: string[] = [];
const EMPTY_VALUE = "-";

type UsageTableRow = TerminalServiceUsageEntry & {
  startTimeLabel: string;
  endTimeLabel: string;
  usageHoursLabel: string;
  pressureLabel?: string;
  isPlaceholder?: boolean;
};

interface UsageActionsMenuProps {
  rowId: string;
  testIdBase: string;
  disabled?: boolean;
  onEdit: () => void;
  onRemove: () => void;
}

export interface UsageViewProps {
  serviceId?: string | null;
  serviceType?: FottServiceTypeValue;
  status?: string | null;
  usageHours?: TerminalServiceUsageEntry[] | null;
  testIdBase?: string;
}

const formatUsageDateTime = (value: string) => {
  const dateTimeInputValue = toDateTimeInputValue(value);

  return dateTimeInputValue
    ? formatLocalDateTime(dateTimeInputValue)
    : formatTerminalServiceDateTime(value);
};

const getUsageEntryHours = (entry: TerminalServiceUsageEntry) =>
  entry.usageHours ?? calculateUsageHours(entry.startTime, entry.endTime) ?? 0;

const formatPressure = (value?: number | null) =>
  value === null || value === undefined ? EMPTY_VALUE : `${value} bar`;

const UsageActionsMenu = ({
  rowId,
  testIdBase,
  disabled = false,
  onEdit,
  onRemove,
}: UsageActionsMenuProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);

  const closeMenu = () => setAnchorEl(null);

  return (
    <>
      <IconButton
        aria-label={t(`${LANG_BASE}.actions`, { defaultValue: "Actions" })}
        size="small"
        disabled={disabled}
        data-testid={`${testIdBase}-actions-menu-${rowId}`}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <MoreVertOutlinedIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          data-testid={`${testIdBase}-edit-${rowId}`}
          onClick={() => {
            closeMenu();
            onEdit();
          }}
        >
          {t(`${LANG_BASE}.edit`, { defaultValue: "Edit" })}
        </MenuItem>
        <MenuItem
          data-testid={`${testIdBase}-remove-${rowId}`}
          onClick={() => {
            closeMenu();
            onRemove();
          }}
        >
          {t(`${LANG_BASE}.remove`, { defaultValue: "Remove" })}
        </MenuItem>
      </Menu>
    </>
  );
};

const UsageView = ({
  serviceId,
  serviceType,
  status,
  usageHours,
  testIdBase = TEST_ID_BASE,
}: UsageViewProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const notificationContext = useContext(NotificationContext);
  const userRoles = useAuthStore((state) => state.user?.roles ?? EMPTY_ROLES);
  const isFottUser = userRoles.includes(FOTT_USER_ROLE);
  const isApproved = status === FottServiceRequestStatus.APPROVED;
  const [entries, setEntries] = useState<TerminalServiceUsageEntry[]>(() => usageHours ?? []);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const submitUsageMutation = useMutation({
    mutationFn: submitTerminalServiceUsageHours,
  });
  const updateUsageMutation = useMutation({
    mutationFn: updateTerminalServiceUsageHours,
  });
  const deleteUsageMutation = useMutation({
    mutationFn: deleteTerminalServiceUsageHours,
  });

  const canManageUsage = isFottUser && isApproved;
  const shouldShowPressure = serviceType === FottServiceType.NITROGEN_SUPPLY_SERVICE;
  const shouldShowAddButton =
    canManageUsage && (shouldShowPressure ? entries.length === 0 : entries.length < MAX_USAGE_ROWS);

  const totalUsageHours = useMemo(
    () => entries.reduce((total, entry) => total + getUsageEntryHours(entry), 0),
    [entries],
  );

  useEffect(() => {
    setEntries(usageHours ?? []);
  }, [usageHours]);

  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setEditingEntryId(null);
  }, []);

  const openAddDrawer = useCallback(() => {
    setEditingEntryId(null);
    setIsDrawerOpen(true);
  }, []);

  const openEditDrawer = useCallback((entry: TerminalServiceUsageEntry) => {
    setEditingEntryId(entry.id);
    setIsDrawerOpen(true);
  }, []);

  const invalidateUsageQueries = useCallback(async () => {
    if (!serviceId || !serviceType) return;

    await queryClient.invalidateQueries({ queryKey: ["fott-services"] });
    await queryClient.invalidateQueries({ queryKey: ["fott-services-preview"] });
    await queryClient.invalidateQueries({
      queryKey: ["terminal-service-details", serviceId, serviceType],
    });
  }, [queryClient, serviceId, serviceType]);

  const notifyUsageAdded = useCallback(() => {
    notificationContext?.notify({
      title: t(`${LANG_BASE}.usage-added-toast`, {
        defaultValue: "You have added a usage time for this service.",
      }),
      type: "success",
    });
  }, [notificationContext, t]);

  const notifyUsageSubmitError = useCallback(
    (error: unknown) => {
      notificationContext?.notify({
        title: t("common.error"),
        description: getTerminalServiceErrorMessage(error) ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
    [notificationContext, t],
  );

  const handleSubmit = useCallback(
    async (values: UsageDrawerSubmitValues) => {
      const normalizedFormValues = {
        startTime: toUtcDateTime(values.startTime),
        endTime: toUtcDateTime(values.endTime),
        ...(values.pressureInBar !== undefined ? { pressureInBar: values.pressureInBar } : {}),
      };

      if (editingEntryId) {
        if (serviceId && serviceType) {
          try {
            await updateUsageMutation.mutateAsync({
              id: serviceId,
              serviceType,
              usageHoursId: editingEntryId,
              payload: normalizedFormValues,
            });
          } catch (error) {
            notifyUsageSubmitError(error);
            return;
          }
        }

        closeDrawer();
        await invalidateUsageQueries();
        return;
      }

      if (serviceId && serviceType) {
        try {
          await submitUsageMutation.mutateAsync({
            id: serviceId,
            serviceType,
            payload: normalizedFormValues,
          });
        } catch (error) {
          notifyUsageSubmitError(error);
          return;
        }
      }

      closeDrawer();
      notifyUsageAdded();
      await invalidateUsageQueries();
    },
    [
      closeDrawer,
      editingEntryId,
      invalidateUsageQueries,
      notifyUsageAdded,
      notifyUsageSubmitError,
      serviceId,
      serviceType,
      submitUsageMutation,
      updateUsageMutation,
    ],
  );

  const removeEntry = useCallback(
    async (id: string) => {
      if (!serviceId || !serviceType) return;

      try {
        await deleteUsageMutation.mutateAsync({
          id: serviceId,
          serviceType,
          usageHoursId: id,
        });
      } catch (error) {
        notifyUsageSubmitError(error);
        return;
      }

      await invalidateUsageQueries();
    },
    [deleteUsageMutation, invalidateUsageQueries, notifyUsageSubmitError, serviceId, serviceType],
  );

  const tableColumns = useMemo<GridColDef<UsageTableRow>[]>(() => {
    const columns: GridColDef<UsageTableRow>[] = [
      {
        field: "startTimeLabel",
        headerName: t(`${LANG_BASE}.columns.start-time`, { defaultValue: "Start time" }),
        flex: 1,
        minWidth: 160,
      },
      {
        field: "endTimeLabel",
        headerName: t(`${LANG_BASE}.columns.end-time`, { defaultValue: "End time" }),
        flex: 1,
        minWidth: 160,
      },
      ...(shouldShowPressure
        ? [
            {
              field: "pressureLabel",
              headerName: t(`${LANG_BASE}.columns.pressure`, { defaultValue: "Pressure (bar)" }),
              flex: 1,
              minWidth: 120,
            } satisfies GridColDef<UsageTableRow>,
          ]
        : []),
      {
        field: "usageHoursLabel",
        headerName: t(`${LANG_BASE}.columns.no-of-hours`, { defaultValue: "No of hours" }),
        flex: 1,
        minWidth: 120,
      },
      {
        field: "action",
        headerName: t(`${LANG_BASE}.columns.actions`, { defaultValue: "Actions" }),
        width: ACTION_COLUMN_WIDTH_PX,
        minWidth: ACTION_COLUMN_WIDTH_PX,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "right",
        headerAlign: "right",
        renderCell: (params: GridRenderCellParams<UsageTableRow>) => {
          if (params.row.isPlaceholder) return null;

          return (
            <UsageActionsMenu
              rowId={params.row.id}
              testIdBase={testIdBase}
              disabled={!canManageUsage}
              onEdit={() => openEditDrawer(params.row)}
              onRemove={() => removeEntry(params.row.id)}
            />
          );
        },
      },
    ];

    return columns;
  }, [canManageUsage, openEditDrawer, removeEntry, shouldShowPressure, t, testIdBase]);
  const tableRows = useMemo<UsageTableRow[]>(() => {
    if (entries.length === 0) {
      return [
        {
          id: "empty-usage-row",
          startTime: "",
          endTime: "",
          startTimeLabel: EMPTY_VALUE,
          endTimeLabel: EMPTY_VALUE,
          pressureLabel: shouldShowPressure ? EMPTY_VALUE : undefined,
          usageHoursLabel: EMPTY_VALUE,
          isPlaceholder: true,
        },
      ];
    }

    return entries.map((entry) => ({
      ...entry,
      startTimeLabel: formatUsageDateTime(entry.startTime),
      endTimeLabel: formatUsageDateTime(entry.endTime),
      pressureLabel: shouldShowPressure ? formatPressure(entry.pressureInBar) : undefined,
      usageHoursLabel: formatUsageHours(getUsageEntryHours(entry)),
    }));
  }, [entries, shouldShowPressure]);
  const visibleTableRows = Math.max(1, Math.min(entries.length, VISIBLE_USAGE_ROWS));
  const tableHeight = ROW_HEIGHT_PX + visibleTableRows * ROW_HEIGHT_PX;
  const totalUsageHoursLabel = `${formatUsageHours(totalUsageHours)} ${t(
    `${LANG_BASE}.hours-suffix`,
    {
      defaultValue: "hrs",
    },
  )}`;
  const editingEntry = entries.find((entry) => entry.id === editingEntryId) ?? null;
  const isUsageSubmitting =
    submitUsageMutation.isPending || updateUsageMutation.isPending || deleteUsageMutation.isPending;
  const totalRowGridTemplateColumns = shouldShowPressure
    ? `minmax(160px, 1fr) minmax(160px, 1fr) minmax(120px, 1fr) minmax(120px, 1fr) ${ACTION_COLUMN_WIDTH_PX}px`
    : `minmax(160px, 1fr) minmax(160px, 1fr) minmax(120px, 1fr) ${ACTION_COLUMN_WIDTH_PX}px`;
  const totalRowMinWidth = shouldShowPressure ? PRESSURE_TABLE_MIN_WIDTH_PX : TABLE_MIN_WIDTH_PX;

  if (!isApproved) {
    return null;
  }

  return (
    <section data-testid={testIdBase} className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-base font-normal text-[#31343B]">
          {t(`${LANG_BASE}.title`, { defaultValue: "Usage" })}
        </h2>

        {shouldShowAddButton && (
          <Button
            variant="outlined"
            size="small"
            disabled={!canManageUsage}
            startIcon={<AddCircleOutlineIcon fontSize="small" />}
            label={
              <span className="normal-case">
                {t(`${LANG_BASE}.add`, { defaultValue: "Add usage" })}
              </span>
            }
            onClick={openAddDrawer}
            testId={`${testIdBase}-add-button`}
          />
        )}
      </div>

      <div className="overflow-hidden border border-[#E5E7EB]">
        <Box sx={{ ...dataGridWrapperSx, height: `${tableHeight}px` }}>
          <DataGrid
            rows={tableRows}
            columns={tableColumns}
            hideFooter
            disableColumnMenu
            disableColumnSorting
            disableColumnResize
            rowHeight={ROW_HEIGHT_PX}
            columnHeaderHeight={ROW_HEIGHT_PX}
            getRowId={(row) => row.id}
            sx={(theme) => ({
              ...(dataGridSx as (currentTheme: typeof theme) => Record<string, unknown>)(theme),
              "& .MuiDataGrid-row:hover": {
                cursor: "default",
              },
            })}
          />
        </Box>

        {entries.length > 0 && (
          <div
            className="grid border-t border-[#E5E7EB] bg-white text-sm font-semibold text-[#31343B]"
            style={{
              gridTemplateColumns: totalRowGridTemplateColumns,
              minWidth: totalRowMinWidth,
            }}
          >
            <div className="flex h-10 items-center px-[10px]" />
            <div className="flex h-10 items-center px-[10px]" />
            {shouldShowPressure && <div className="flex h-10 items-center px-[10px]" />}
            <div className="flex h-10 items-center px-[10px]" data-testid={`${testIdBase}-total`}>
              {t(`${LANG_BASE}.total`, { defaultValue: "Total" })}: {totalUsageHoursLabel}
            </div>
            <div className="flex h-10 items-center px-[10px]" />
          </div>
        )}
      </div>

      <UsageDrawer
        isOpen={isDrawerOpen}
        mode={editingEntryId ? "edit" : "add"}
        entries={entries}
        initialEntry={editingEntry}
        serviceType={serviceType}
        testIdBase={testIdBase}
        isSubmitting={isUsageSubmitting}
        onClose={closeDrawer}
        onSubmit={handleSubmit}
      />
    </section>
  );
};

export { calculateUsageHours, formatUsageHours };
export default UsageView;
